import Component, { mixins } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'

import WithRender from './InscriptionLieuExamenForm.html'

import { 
  AbstractControl,
  ControlValidator,
  ControlValidatorValidationType,
  FormControl,
  FormGroup,
  MButton,
  MButtonSkin,
  MDialog,
  MDialogState,
  MDialogWidth,
  MDropdown,
  MDropdownItem,
  MForm,
  MIcon,
  MMessage,
  MMessageSkin,
  MMessageState,
  MProgress,
  MRadio,
  MRadioGroup,
  MTextfield,
  RequiredValidator,
} from '@ulaval/modul-components'

import LocationSvg from '@/modules/global/media/svg/location/LocationSvg'

import IFormInscriptionLieuExamen, { IConflitHoraireInscriptionLieuExamen } from './IFormInscriptionLieuExamen.model'
import ITypeInscriptionLieuExamen, { SLUGS as ITypeInscriptionLieuExamen_SLUGS } from '../ITypeInscriptionLieuExamen.model'
import { ICity } from '@/modules/global/localization/city/ICity.model'
import { ICountry } from '@/modules/global/localization/country/ICountry.model'
import { StatutInscription } from '../IDossierEtudiant.model';

import { STORE_GETTERS } from '@/store'

import EventBus from '@/EventBus'

import { IUpdateInscriptionLieuExamenRequestPayload, EVENTS as InscriptionLieuExamenService_EVENTS } from '../InscriptionLieuExamen.service'
import ROUTES from '@/router/ROUTES'

export enum EVENTS {
  UPDATE_TYPE_INSCRIPTION_LIEU_EXAMEN = 'update:type-inscription-lieu-examen',
  UPDATE_INSCRIPTION_LIEU_EXAMEN = 'update-inscription-lieu-examen'
}

export interface IUpdateTypeInscriptionLieuExamen {
  identifier: number;
}

export interface IUpdateInscriptionLieuExamen {
  identifier: number;
  stateChanged: boolean;
  component: InscriptionLieuExamenForm;
}

@WithRender
@Component({
  components: {
    LocationSvg,
    MButton,
    MDialog,
    MDropdown,
    MDropdownItem,
    MForm,
    MIcon,
    MMessage,
    MProgress,
    MRadio,
    MRadioGroup,
    MTextfield
  }
})
export default class InscriptionLieuExamenForm extends mixins() {
  public formGroup: FormGroup = new FormGroup({
    'centreExamenDisabled': new FormControl<number>([])
  })
  private stateChanged: boolean = false
  private isRepondantInformationsModalOpen: boolean = false
  private typeInscriptionLieuExamenInMemory: number | null = null

  private readonly ITypeInscriptionLieuExamen_SLUGS: any = ITypeInscriptionLieuExamen_SLUGS
  private readonly MMessageState: object = MMessageState
  private readonly MMessageSkin: object = MMessageSkin
  private readonly MDialogState: object = MDialogState
  private readonly MDialogWidth: object = MDialogWidth
  private readonly MButtonSkin: object = MButtonSkin
  private readonly StatutInscription: object = StatutInscription
  private readonly VALIDATOR_KEY_VILLE_ALIAS: string = 'validation[ville-alias]'

  @Prop({ required: true })
  private inscriptionLieuExamen!: IFormInscriptionLieuExamen

  private get articleClasses(): object {
    if (!this.inscriptionLieuExamen) {
      return {}
    }

    return {
      'inscription-lieu-examen-form--disabled': this.inscriptionLieuExamen.isDesactiver,
      'inscription-lieu-examen-form--timestamp-not-published': !this.inscriptionLieuExamen.horodateCampus && !this.inscriptionLieuExamen.horodateHorsCampus
    }
  }

  private get typesInscriptionLieuExamen(): Array<ITypeInscriptionLieuExamen> {
    return this.$store.getters[STORE_GETTERS.INSCRIPTION_LIEU_EXAMEN_GET_TYPES_INSCRIPTION_LIEU_EXAMEN] || []
  }

  private get typesInscriptionLieuExamenBySlug(): { [key: string]: ITypeInscriptionLieuExamen } {
    return this.$store.getters[STORE_GETTERS.INSCRIPTION_LIEU_EXAMEN_GET_TYPES_INSCRIPTION_LIEU_EXAMEN_BY_SLUG] || []
  }

  private get isTypeInscriptionLieuExamenLoadingStateActive(): boolean {
    return this.$store.getters[STORE_GETTERS.INSCRIPTION_LIEU_EXAMEN_GET_IS_TYPE_INSCRIPTION_LIEU_EXAMEN_LOADING_STATE_ACTIVE]
  }

  private get cities(): Array<ICity> {
    return this.$store.getters[STORE_GETTERS.CITY_GET_CITIES] || []
  }

  private get citiesNames(): Array<string> {
    return this.$store.getters[STORE_GETTERS.CITY_GET_CITIES_NAMES] || []
  }

  private get countries(): Array<ICountry> {
    return this.$store.getters[STORE_GETTERS.COUNTRY_GET_COUNTRIES]
  }

  public get identifier(): number {
    return this.inscriptionLieuExamen ? this.inscriptionLieuExamen.id || -1 : -1
  }

  private get isFormAvailable(): boolean {
    if (!this.inscriptionLieuExamen) {
      return false
    }

    return !this.inscriptionLieuExamen.isDesactiver &&
      (
        this.inscriptionLieuExamen.statutInscription === StatutInscription.INSCRIPTION_EN_ATTENTE || 
        this.inscriptionLieuExamen.statutInscription === StatutInscription.INSCRIPTION_COMPLETEE
      )
  }

  public get typeInscriptionLieuExamenField(): AbstractControl<number> | undefined {
    try {
      return this.formGroup.getControl<number>('typeInscriptionLieuExamen');
    } catch (error) {
      return undefined
    }
  }

  public get centreExamenDropdown(): AbstractControl<number> | undefined {
    try {
      return this.formGroup.getControl<number>('centreExamen');
    } catch (error) {
      return undefined
    }
  }

  public get centreExamenDisabledDropdown(): AbstractControl<number> | undefined {
    try {
      return this.formGroup.getControl<number>('centreExamenDisabled');
    } catch (error) {
      return undefined
    }
  }

  public get villeRepondantField(): AbstractControl<string> | undefined {
    try {
      return this.formGroup.getControl<string>('villeRepondant');
    } catch (error) {
      return undefined
    }
  }

  public get paysRepondantDropdown(): AbstractControl<string> | undefined {
    try {
      return this.formGroup.getControl<string>('paysRepondant');
    } catch (error) {
      return undefined
    }
  }

  private get villeRepondantFieldErrorMessage(): string | undefined {
    if (this.villeRepondantField) {
      for (const city of this.cities) {
        if (
          !city.nom.localeCompare(this.villeRepondantField.value, undefined, { sensitivity: 'base' }) ||
          this.cityAliasContainsValue(city, this.villeRepondantField.value)
        ) {
          return city.dossierExamenValidationAliasMessage
        }
      }
    }

    return undefined
  }

  @Watch('typeInscriptionLieuExamenField.value', { immediate: true })
  @Watch('typesInscriptionLieuExamenBySlug')
  private async onTypeInscriptionLieuExamenFieldChange(): Promise<void> {
    EventBus.$emit(EVENTS.UPDATE_TYPE_INSCRIPTION_LIEU_EXAMEN, {
      identifier: this.identifier
    } as IUpdateTypeInscriptionLieuExamen )

    if (this.isFormAvailable) {
      if (!this.formGroup.containsControl('typeInscriptionLieuExamen')) {
        this.formGroup.addControl('typeInscriptionLieuExamen', new FormControl<number>([RequiredValidator()]))
      }
  
      if (Object.keys(this.typesInscriptionLieuExamenBySlug).length > 0) {
        switch (this.typeInscriptionLieuExamenField && this.typeInscriptionLieuExamenField.value) {
          case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.CAMPUS_UL].id:
            if (this.typeInscriptionLieuExamenField) {
              this.typeInscriptionLieuExamenInMemory = this.typeInscriptionLieuExamenField.value
            }

            this.formGroup.removeControl('centreExamen')
            this.formGroup.removeControl('paysRepondant')
            this.formGroup.removeControl('villeRepondant')

            if (!this.formGroup.containsControl('centreExamenDisabled')) {
              this.formGroup.addControl('centreExamenDisabled', new FormControl<number>([]))
            }

            break
          case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.CENTRE_EXAMEN].id:
            if (this.typeInscriptionLieuExamenField) {
              this.typeInscriptionLieuExamenInMemory = this.typeInscriptionLieuExamenField.value
            }

            this.formGroup.removeControl('centreExamenDisabled')
            this.formGroup.removeControl('paysRepondant')
            this.formGroup.removeControl('villeRepondant')

            this.formGroup.addControl('centreExamen', new FormControl<number>([RequiredValidator()]))

            break
          case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.REPONDANT].id:
            this.formGroup.removeControl('centreExamen')

            this.formGroup.addControl('paysRepondant', new FormControl<string>([RequiredValidator()]))
            this.formGroup.addControl('villeRepondant', new FormControl<string>([
              RequiredValidator(),
              {
                key: this.VALIDATOR_KEY_VILLE_ALIAS,
                validationType: ControlValidatorValidationType.OnGoing,
                error: {
                  message: this.villeRepondantFieldErrorMessage as string
                },
                validationFunction: this.villeRepondantCityNameValidator
              }
            ]))
            if (!this.formGroup.containsControl('centreExamenDisabled')) {
              this.formGroup.addControl('centreExamenDisabled', new FormControl<number>([]))
            }
            
            break
          default:
            break
        }
      }
    }
  }

  @Watch('inscriptionLieuExamen', { immediate: true })
  private onInscriptionLieuExamenChange(): void {
    if (this.typeInscriptionLieuExamenField && this.inscriptionLieuExamen.situation.idTypeInscriptionLieuExamen) {
      if (this.inscriptionLieuExamen.statutInscription !== StatutInscription.INSCRIPTION_EN_ATTENTE) {
        this.typeInscriptionLieuExamenField.value = this.inscriptionLieuExamen.situation.idTypeInscriptionLieuExamen
      }
    }
  }

  @Watch('centreExamenDropdown', { immediate: true })
  private onCentreExamenDropdownChange(): void {
    this.$nextTick()
      .then(() => {
        if (this.centreExamenDropdown && this.inscriptionLieuExamen.situation.idVille) {
          this.centreExamenDropdown.value = this.inscriptionLieuExamen.situation.idVille
        }
      })
  }

  @Watch('villeRepondantField', { immediate: true })
  private onVilleRepondantFieldChange(): void {
    this.$nextTick()
      .then(() => {
        if (this.villeRepondantField && this.inscriptionLieuExamen.situation.villeRepondant) {
          this.villeRepondantField.value = this.inscriptionLieuExamen.situation.villeRepondant
        }
      })
  }

  @Watch('paysRepondantDropdown', { immediate: true })
  private onPaysRepondantDropdownChange(): void {
    this.$nextTick()
      .then(() => {
        if (this.paysRepondantDropdown && this.inscriptionLieuExamen.situation.paysRepondant) {
          this.paysRepondantDropdown.value = this.inscriptionLieuExamen.situation.paysRepondant
        }
      })
  }

  private mounted(): void {
    this.afterInscriptionLieuExamenUpdate()
  }

  private afterInscriptionLieuExamenUpdate(): void {
    EventBus.$on(InscriptionLieuExamenService_EVENTS.AFTER_UPDATE, () => {
      this.stateChanged = false
    })
  }

  public getFormData(): IUpdateInscriptionLieuExamenRequestPayload | undefined {
    if (this.typeInscriptionLieuExamenField) {
      switch (this.typeInscriptionLieuExamenField.value) {
        case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.CAMPUS_UL].id:
          return {
            idInscriptionLieuExamen: this.inscriptionLieuExamen.id,
            idTypeInscriptionLieuExamen: this.typeInscriptionLieuExamenField.value,
            slugTypeInscriptionLieuExamen: ITypeInscriptionLieuExamen_SLUGS.CAMPUS_UL,
            idVille: null,
            paysRepondant: null,
            villeRepondant: null
          } as IUpdateInscriptionLieuExamenRequestPayload
        case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.CENTRE_EXAMEN].id:
          return {
            idInscriptionLieuExamen: this.inscriptionLieuExamen.id,
            idTypeInscriptionLieuExamen: this.typeInscriptionLieuExamenField.value,
            slugTypeInscriptionLieuExamen: ITypeInscriptionLieuExamen_SLUGS.CENTRE_EXAMEN,
            idVille: this.centreExamenDropdown ? this.centreExamenDropdown.value || null : null,
            paysRepondant: null,
            villeRepondant: null
          } as IUpdateInscriptionLieuExamenRequestPayload
        case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.REPONDANT].id:
          return {
            idInscriptionLieuExamen: this.inscriptionLieuExamen.id,
            idTypeInscriptionLieuExamen: this.typeInscriptionLieuExamenField.value,
            slugTypeInscriptionLieuExamen: ITypeInscriptionLieuExamen_SLUGS.REPONDANT,
            idVille: null,
            paysRepondant: this.paysRepondantDropdown ? this.paysRepondantDropdown.value || null : null,
            villeRepondant: this.villeRepondantField ? this.villeRepondantField.value || null : null
          } as IUpdateInscriptionLieuExamenRequestPayload
        default:
          break
      }
    }

    return undefined
  }

  private onInscriptionLieuExamenUpdate(): void {
    this.stateChanged = false

    if (this.typeInscriptionLieuExamenField) {
      if (
        this.typeInscriptionLieuExamenField.value !== this.inscriptionLieuExamen.situation.idTypeInscriptionLieuExamen ||
        this.inscriptionLieuExamen.statutInscription === StatutInscription.INSCRIPTION_EN_ATTENTE
      ) {
        this.stateChanged = true
      }
  
      switch (this.typeInscriptionLieuExamenField.value) {
        case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.CAMPUS_UL].id:
          this.isRepondantInformationsModalOpen = false
          this.typeInscriptionLieuExamenInMemory = this.typeInscriptionLieuExamenField.value

          break
        case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.CENTRE_EXAMEN].id:
          this.isRepondantInformationsModalOpen = false
          this.typeInscriptionLieuExamenInMemory = this.typeInscriptionLieuExamenField.value

          if (this.centreExamenDropdown && this.centreExamenDropdown.value !== this.inscriptionLieuExamen.situation.idVille) {
            this.stateChanged = true
          }
          break
        case this.typesInscriptionLieuExamenBySlug[ITypeInscriptionLieuExamen_SLUGS.REPONDANT].id:
          this.isRepondantInformationsModalOpen = true

          if (
            (this.villeRepondantField && this.villeRepondantField.value !== this.inscriptionLieuExamen.situation.villeRepondant) ||
            (this.paysRepondantDropdown && this.paysRepondantDropdown.value !== this.inscriptionLieuExamen.situation.paysRepondant)
          ) {
            this.stateChanged = true
          }
          break
        default:
          break
      }
    }

    EventBus.$emit(EVENTS.UPDATE_INSCRIPTION_LIEU_EXAMEN, {
      identifier: this.inscriptionLieuExamen.id,
      stateChanged: this.stateChanged,
      component: this
    } as IUpdateInscriptionLieuExamen)
  }

  private generateConflitHoraireExamsLabel(inscriptionLieuExamen: IFormInscriptionLieuExamen): string {
    const examsLabel: Array<string> = []

    this.inscriptionLieuExamen.conflitsHoraire.forEach((conflitHoraire: IConflitHoraireInscriptionLieuExamen) => {
      examsLabel.push(`${conflitHoraire.codeCours} ${conflitHoraire.sectionCours}`)
    })

    return examsLabel.join(', ')
  }

  private onInformationModalCancelButtonClick(): void {
    if (this.typeInscriptionLieuExamenField && this.typeInscriptionLieuExamenInMemory) {
      this.typeInscriptionLieuExamenField.value = this.typeInscriptionLieuExamenInMemory

      this.onInscriptionLieuExamenUpdate()
    }
  }

  private villeRepondantCityNameValidator(self: AbstractControl): boolean { 
    for (const city of this.cities) {
      if (
        !city.nom.localeCompare(self.value.trim(), undefined, { sensitivity: 'base' }) ||
        this.cityAliasContainsValue(city, self.value)
      ) {
        if (this.villeRepondantField) {
          const matchingValidator: ControlValidator | undefined = this.villeRepondantField.validators.find((validator: ControlValidator) => validator.key === this.VALIDATOR_KEY_VILLE_ALIAS)

          if (matchingValidator) {
            matchingValidator.error.message = city.dossierExamenValidationAliasMessage
          }
        }
        return false
      }
    }

    return true
  }

  private cityAliasContainsValue(city: ICity, value: string): boolean {
    for (const alias of city.alias) {
      if (!alias.localeCompare(value.trim(), undefined, { sensitivity: 'base' })) {
        return true
      }
    }

    return false
  }

  private onDisplayGestionFicheRepondantButtonClick(): void {
    this.mixin_router_navigate({
      name: ROUTES.FICHE_REPONDANT_OVERVIEW,
      query: this.$route.query
    })
  }
}
